import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeliveryNote } from '../../services/api';
import { getPendingDeliveryNote, DELIVERY_NOTES_CHANGED_EVENT } from '../../services/deliveryNotes';
import type { QueuedDeliveryNote } from '../../services/db';
import type { DeliveryNote } from '../../types';
import { useDeliveryNoteActions } from './useDeliveryNoteActions';
import './DeliveryNoteSection.css';

/**
 * "Documento de transporte (PDF)" de la ficha del contenedor (2026-10-23): el
 * DDT que el proveedor tiene en papel o en el correo. Uno por contenedor; subir
 * otro lo sustituye. Va al momento, sin esperar al botón Guardar de la ficha,
 * y sin conexión queda pendiente y sale solo al volver la red.
 *
 * Necesita el contenedor ya creado en el servidor: hasta entonces (ficha nueva
 * o creada sin conexión y aún sin sincronizar) solo enseña el aviso.
 */
export function DeliveryNoteSection({
  idCargoDetail,
  idCargo,
  isOnline,
}: {
  /** El id del contenedor en el servidor, o undefined si aún no existe. */
  idCargoDetail?: number;
  idCargo: number;
  isOnline: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState<DeliveryNote | null>(null);
  const [pending, setPending] = useState<QueuedDeliveryNote | null>(null);
  // Hasta qué petición ha respondido el servidor: con red y sin respuesta aún,
  // se está cargando.
  const [answered, setAnswered] = useState<string | null>(null);

  const ready = typeof idCargoDetail === 'number' && idCargoDetail > 0;

  const refreshPending = useCallback(() => {
    if (!ready) return Promise.resolve();
    return getPendingDeliveryNote(idCargoDetail!)
      .then((p) => setPending(p))
      .catch(() => setPending(null));
  }, [ready, idCargoDetail]);

  const { busy, message, pick, remove, discard, fileInput } = useDeliveryNoteActions(idCargoDetail, idCargo, (next) => {
    if (next !== undefined) setNote(next);
    void refreshPending();
  });

  // Lo que hay en el servidor, cada vez que hay conexión (y al volver, o al
  // vaciarse lo pendiente).
  const requestKey = ready && isOnline ? `${idCargoDetail}:${pending?.queuedAt ?? ''}` : null;
  const loading = requestKey !== null && answered !== requestKey;
  useEffect(() => {
    if (!requestKey) return;
    let cancelled = false;
    getDeliveryNote(idCargoDetail!)
      .then((r) => { if (!cancelled) setNote(r.data ? r.data : null); })
      .catch(() => { /* sin respuesta: se queda lo último que se vio */ })
      .finally(() => { if (!cancelled) setAnswered(requestKey); });
    return () => { cancelled = true; };
  }, [requestKey, idCargoDetail]);

  useEffect(() => {
    let alive = true;
    const load = () => { if (alive) void refreshPending(); };
    queueMicrotask(load);
    window.addEventListener(DELIVERY_NOTES_CHANGED_EVENT, load);
    return () => { alive = false; window.removeEventListener(DELIVERY_NOTES_CHANGED_EVENT, load); };
  }, [refreshPending]);

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
  };

  // Lo pendiente manda sobre lo que se vio en el servidor: es lo último que
  // pidió el usuario.
  const showServerNote = note && !pending;

  return (
    <section className="edit-section delivery-note">
      <h3 className="edit-section__title">{t('deliveryNote.title')}</h3>

      {!ready ? (
        <p className="delivery-note__hint">{t('deliveryNote.saveFirst')}</p>
      ) : (
        <>
          {pending && (
            <div className={`delivery-note__row ${pending.error ? 'delivery-note__row--error' : 'delivery-note__row--pending'}`}>
              <PdfIcon />
              <div className="delivery-note__info">
                <span className="delivery-note__name">
                  {pending.op === 'upload' ? pending.fileName : t('deliveryNote.pendingRemoveTitle')}
                </span>
                <span className="delivery-note__meta">
                  {pending.error
                    ? t('deliveryNote.rejected', { reason: pending.error })
                    : t(pending.op === 'upload' ? 'deliveryNote.pendingUpload' : 'deliveryNote.pendingRemove')}
                </span>
              </div>
              <button type="button" className="delivery-note__btn delivery-note__btn--ghost" onClick={discard}>
                {t('deliveryNote.discard')}
              </button>
            </div>
          )}

          {showServerNote && (
            <div className="delivery-note__row">
              <PdfIcon />
              <div className="delivery-note__info">
                <span className="delivery-note__name">{note.fileName}</span>
                <span className="delivery-note__meta">
                  {formatDate(note.dateUpload) && t('deliveryNote.uploadedAt', { date: formatDate(note.dateUpload) })}
                </span>
              </div>
              {isOnline ? (
                <a className="delivery-note__btn" href={note.url} target="_blank" rel="noreferrer noopener">
                  {t('deliveryNote.view')}
                </a>
              ) : (
                <span className="delivery-note__meta">{t('deliveryNote.viewOffline')}</span>
              )}
            </div>
          )}

          {loading && !note && !pending && (
            <div className="delivery-note__hint">
              <span className="spinner spinner--green" /> {t('deliveryNote.loading')}
            </div>
          )}

          <div className="delivery-note__actions">
            <button type="button" className="photo-add-btn" onClick={pick} disabled={busy !== null}>
              {busy === 'upload' ? <span className="btn-spinner" aria-hidden="true" /> : <PdfIcon small />}
              {showServerNote || pending?.op === 'upload' ? t('deliveryNote.replace') : t('deliveryNote.upload')}
            </button>
            {showServerNote && (
              <button type="button" className="photo-add-btn photo-add-btn--select" onClick={remove} disabled={busy !== null}>
                {busy === 'remove' ? <span className="btn-spinner" aria-hidden="true" /> : null}
                {t('deliveryNote.remove')}
              </button>
            )}
          </div>
          {!note && !pending && !loading && (
            <p className="delivery-note__hint">{t('deliveryNote.immediate')}</p>
          )}
          {message && (
            <p className={`delivery-note__message delivery-note__message--${message.kind}`} role="status">{message.text}</p>
          )}
          {fileInput}
        </>
      )}
    </section>
  );
}

export function PdfIcon({ small }: { small?: boolean }) {
  const size = small ? 18 : 28;
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} aria-hidden="true" className={small ? undefined : 'delivery-note__icon'}>
      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8.5 13h1.2c.9 0 1.6.6 1.6 1.5S10.6 16 9.7 16h-.5v1.5h-.7V13zm.7.6v1.8h.5c.5 0 .9-.4.9-.9s-.4-.9-.9-.9h-.5zm2.8-.6h1.3c1.2 0 2 .9 2 2.2s-.8 2.3-2 2.3H12V13zm.7.6v3.3h.6c.8 0 1.3-.6 1.3-1.7s-.5-1.6-1.3-1.6H12.7zm3-.6h2.5v.6h-1.8v1.2h1.6v.6h-1.6v1.9h-.7V13z" />
    </svg>
  );
}
